import { useState } from 'react';
import { T } from '../../design';
import type { Course } from '../../types/courses.types';
import { parseLocalDate } from '../../utils/date';

export interface AvailInfo {
  label: string;
  tone: 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'accent';
  disabled?: boolean;
  full?: boolean;
}

/** Ledige pladser: foretræk server-beregnet availableSpots, ellers udled. */
function spots(c: Course): number | null {
  if (c.availableSpots != null) return c.availableSpots;
  if (c.maxParticipants == null) return null;
  return c.maxParticipants - c.enrolledCount;
}

function isPast(c: Course): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return parseLocalDate(c.courseDate) < today;
}

/**
 * Porteret fra design availInfo, men drevet af rigtige Course-felter.
 * Viser kun OM der er ledige pladser — aldrig antallet.
 */
export function availInfo(c: Course): AvailInfo {
  if (isPast(c)) return { label: 'Afsluttet', tone: 'neutral', disabled: true };
  const a = spots(c);
  if (a != null && a <= 0) return { label: 'Fuldt booket', tone: 'danger', full: true };
  return { label: 'Ledig', tone: 'success' };
}

export interface CourseImageProps {
  src: string | null | undefined;
  alt: string;
  height?: number;
  radius?: number;
}

/**
 * 4:5 kursusbillede. Rigtigt billede med lazy-load; falder tilbage til det
 * stribede placeholder hvis src mangler eller billedet fejler at loade.
 */
export function CourseImage({ src, alt, height = 200, radius = 14 }: CourseImageProps) {
  const [broken, setBroken] = useState(false);
  const showImg = !!src && !broken;
  if (showImg) {
    return (
      <img
        src={src!}
        alt={alt}
        loading="lazy"
        onError={() => setBroken(true)}
        style={{ width: '100%', height, borderRadius: radius, objectFit: 'cover', border: `1px solid ${T.border}`, display: 'block', flexShrink: 0 }}
      />
    );
  }
  return (
    <div style={{ width: '100%', height, borderRadius: radius, overflow: 'hidden', border: `1px solid ${T.border}`, background: `repeating-linear-gradient(135deg, ${T.tertiary} 0 9px, ${T.surface} 9px 18px)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.fg3, fontFamily: 'ui-monospace, monospace', background: T.surface, padding: '3px 9px', borderRadius: 6 }}>{alt} · 4:5</span>
    </div>
  );
}

/** Hjælper: første billede til kort, hele listen til detalje-karrusel. */
export function courseImages(c: Course): string[] {
  if (c.images && c.images.length > 0) return c.images;
  return c.imageUrl ? [c.imageUrl] : [];
}
